// src/input/inputManager.ts — Girdi kaynaklarını (klavye+mouse, gamepad) tek `InputFrame`'e indirger (WP6; ARCHITECTURE §3 kural 4,
// DESIGN §2/§11). DOM'suz çekirdek: `reduce(raw)` ham durumu alır, kenar bitlerini BİRİKTİRİR (latched |= edge); `consume(out)`
// biriktirileni out'a yazıp sıfırlar — 0-tick karede basış kaybolmaz, 2-tick karede çift basış olmaz. Sıcak yolda tahsis yok:
// tek `RawState` nesnesi kurulumda yaratılır, kaynaklar onu her poll'da doldurur. three import ETMEZ (sim gibi düz sayılar).
import type { InputFrame } from '../core/types.ts';
import { Btn } from '../core/types.ts';
import { T } from '../config/tuning.ts';
import { smoothstep } from '../core/math.ts';

/** Ham girdi durumu: kaynaklar (keyboard.ts, gamepad.ts) her poll'da bunu doldurur; testler elle kurar. Btn maskeleri. */
export interface RawState {
  /** Şu an basılı Btn bitleri — tüm kaynakların birleşimi (klavye | mouse | pad). */
  buttons: number;
  /** Son poll'dan beri OLAY düzeyinde görülen basma/bırakma kenarları (iki poll arasında başlayıp biten dokunuş kaybolmasın).
   *  Klavye/mouse doldurur; pad için 0 kalır (pad poll edilir, kenarını reduce prev/cur farkından bulur). */
  pressedSince: number;
  releasedSince: number;
  /** Son poll'dan beri MOUSE tuşundan gelen basma kenarları (fromMouse / aimYaw geçerliliği). pressedSince'in alt kümesi. */
  mousePressedSince: number;
  /** Klavye hareket ekseni ham toplamı (WASD; −1..1 bileşenler, normalize edilmemiş). Kamera uzayı: x sağ, z aşağı (S). */
  keyMoveX: number; keyMoveZ: number;
  /** Pad sol çubuk ham eksenleri (ölü bölge uygulanmamış; y ileri = −1). Pad yoksa 0. */
  padAxisX: number; padAxisY: number;
  /** İmleç NDC (−1..1; y yukarı +). Bakış çözümleyiciye (aimResolver) geçirilir. */
  mouseNdcX: number; mouseNdcY: number;
  /** Her mousemove'da artar; reduce bunu önceki değerle karşılaştırıp "mouse hareket etti" tick'ini işaretler. */
  mouseMoveSeq: number;
}
export const rawState = (): RawState => ({
  buttons: 0, pressedSince: 0, releasedSince: 0, mousePressedSince: 0,
  keyMoveX: 0, keyMoveZ: 0, padAxisX: 0, padAxisY: 0, mouseNdcX: 0, mouseNdcY: 0, mouseMoveSeq: 0,
});
/** Bir poll için sıfırlanması gereken alanlar (kaynaklar OR'lar/toplar; seq ve NDC kalıcı — kaynak yalnız değişince yazar). */
export function rawClear(raw: RawState): void {
  raw.buttons = 0; raw.pressedSince = 0; raw.releasedSince = 0; raw.mousePressedSince = 0;
  raw.keyMoveX = 0; raw.keyMoveZ = 0; raw.padAxisX = 0; raw.padAxisY = 0;
}

/** Bir girdi kaynağı: her poll'da ham duruma katkısını OR'lar / toplar (tahsissiz). keyboard.ts ve gamepad.ts uygular. */
export interface InputSource { read(raw: RawState): void }

/** İmleç NDC → istenen bakış yaw'ı (dünya). WP9 kamera + oyuncu konumuyla kurar (zemin düzlemi ışını → atan2). NaN dönebilir. */
export type AimResolver = (ndcX: number, ndcY: number) => number;

/** Saldırı bitleri: mouse kaynağı bunlara basınca `fromMouse = true` (DESIGN §2 aimYaw kuralı b). */
const ATTACK_BITS: number = Btn.Light | Btn.Heavy;
/** Taunt yalnız T / D-pad↓ — akor (L1+R1) YOK; savunma amaçlı: hiçbir bit birleşimi Taunt'a dönüşmez (reduce bit üretmez). */

/**
 * Ölü bölge: |v| < dead → 0; aksi hâlde büyüklük smoothstep(dead, 1, |v|) ile yeniden ölçeklenir, yön korunur, |v| ≤ 1.
 * DESIGN §2 (0.18) — pad ve klavye toplamı aynı yoldan geçer (MOVE.walkDead ile aynı sayı).
 */
export function applyDeadzone(out: { x: number; z: number }, x: number, z: number, dead: number): void {
  const m = Math.hypot(x, z);
  if (m < dead || m === 0) { out.x = 0; out.z = 0; return; }
  const s = smoothstep(dead, 1, m) / m;
  out.x = x * s; out.z = z * s;
}

/** Kamera-relative çubuk → dünya XZ. cameraYaw 0: x → dünya x, y (ileri −1) → dünya −z (ekranın üstü = uzak kenar). */
export function cameraToWorld(out: { x: number; z: number }, sx: number, sz: number, cameraYaw: number): void {
  if (cameraYaw === 0) { out.x = sx; out.z = sz; return; }
  // Kameranın ileri yönü (dünya) yaw 0'da (0, −1); sağ yön (1, 0). Y ekseni etrafında döndür.
  const c = Math.cos(cameraYaw), s = Math.sin(cameraYaw);
  const fwdX = -s, fwdZ = -c;      // ileri
  const rightX = c, rightZ = -s;   // sağ
  const f = -sz;                   // çubuk ileri bileşeni (y −1 = ileri)
  out.x = rightX * sx + fwdX * f;
  out.z = rightZ * sx + fwdZ * f;
}

const scratchMove = { x: 0, z: 0 };

/**
 * Klavye kenar freni tavanı (tur-2 bulgusu). WASD her zaman tam itme (1.0 ≥ EDGE.brakeThreshold 0.60) ürettiğinden klavye
 * oyuncusu DESIGN §3 kenar frenini hiç alamıyordu (A basılı → ~1 s'de kendi kendine düşüş); pad oyuncusu çubuğu yarım basarak
 * durabiliyor. Çözüm: oyuncu kenar alarm bölgesindeyken (game.ts `keyEdgeBrake` bayrağını edgeDist < EDGE.alarmDist ∧ grounded
 * ile kurar) ve pad çubuğu boştayken klavye hareket büyüklüğü 0.55'e kırpılır (bot `edgeAwareness` ile aynı sayı, DESIGN bot
 * bölümü) → fren devrede, karakter kenarda durur. Bilinçli düşme: zıplama (Air'de fren yok) ya da pad çubuğu.
 * Sayı 3. turda tuning'e taşındı: T.EDGE.keyBrakeMove (0.55; eşiğin ALTINDA olmalı) — yerel sabit kalktı.
 */
const KEY_EDGE_MOVE_MAX: number = T.EDGE.keyBrakeMove;

/**
 * Kenar freni koşulu (DESIGN §3): oyuncu YERDE ∧ kenara < EDGE.alarmDist. game.ts her karede `poll()`'dan ÖNCE
 * `im.keyEdgeBrake = keyEdgeBrakeOn(playerFighter)` yazar; oyuncu yoksa (duman/izleyici, playerId < 0) kapalı.
 * Kural burada durur ki kırpmayla (reduce) aynı dosyada test edilebilsin — çağrı yeri unutulunca fren sessizce ölüyordu (3. tur).
 */
export function keyEdgeBrakeOn(f: { grounded: boolean; edgeDist: number } | undefined): boolean {
  return f !== undefined && f.grounded && f.edgeDist < T.EDGE.alarmDist;
}

/**
 * Girdi yöneticisi (oyuncu başına bir tane; pad indeksi = oyuncu indeksi, ileride yerel çok oyuncu).
 * Kullanım (WP9 game.ts): `poll()` rAF başında bir kez; her sim tick'inde `consume(inputOf(world, playerId))`.
 */
export class InputManager {
  /** Kamera yaw'ı (DESIGN §10: sabit 0). WP9 kamera dönerse günceller. */
  cameraYaw = 0;
  /** Ölü bölge ve mouse tazelik eşiği (tuning'den; test değiştirebilir). */
  deadzone: number = T.INPUT.deadzone;
  mouseFreshTicks: number = T.AIM.mouseFreshTicks;
  /** consume sayacı = sim tick'i (mouse tazeliği bu sayaçla ölçülür; sim tick'ine paralel ilerler). */
  tick = 0;
  /** Klavye kenar freni bayrağı: game.ts her karede poll'dan ÖNCE kurar (oyuncu yerde ∧ edgeDist < EDGE.alarmDist).
   *  true iken yalnız klavyeden gelen hareket |v| ≤ KEY_EDGE_MOVE_MAX'a kırpılır (pad çubuğu dokunulmamış). Varsayılan kapalı. */
  keyEdgeBrake = false;

  readonly raw: RawState = rawState();
  private readonly sources: InputSource[] = [];
  private aimResolver: AimResolver | null = null;

  // Son reduce'un durumu (buttons/move her consume'da olduğu gibi yazılır; kenarlar biriktirilir).
  private prevButtons = 0;
  private buttons = 0;
  private latchedPressed = 0;
  private latchedReleased = 0;
  private latchedMousePressed = 0;
  private moveX = 0;
  private moveZ = 0;
  private mouseNdcX = 0;
  private mouseNdcY = 0;
  private lastMouseSeq = 0;
  /** Mouse'un son hareket ettiği tick; hiç hareket etmediyse çok eski. */
  private lastMouseMoveTick = -1_000_000;

  addSource(src: InputSource): void { this.sources.push(src); }
  removeSource(src: InputSource): void { const i = this.sources.indexOf(src); if (i >= 0) this.sources.splice(i, 1); }
  setAimResolver(fn: AimResolver | null): void { this.aimResolver = fn; }

  /** rAF başında bir kez: kaynakları oku (tek ham nesneye), indirge. */
  poll(): void {
    rawClear(this.raw);
    for (let i = 0; i < this.sources.length; i++) this.sources[i]!.read(this.raw);
    this.reduce(this.raw);
  }

  /**
   * DOM'suz çekirdek: ham durumdan kenarları çıkar ve BİRİKTİR. Birden çok poll arasında consume gelmezse
   * (0-tick kare) kenarlar korunur; aynı basış iki kez sayılmaz çünkü kenar prev/cur farkından bir kez doğar.
   */
  reduce(raw: RawState): void {
    const cur = raw.buttons;
    const pressedEdge = (cur & ~this.prevButtons) | raw.pressedSince;
    const releasedEdge = (this.prevButtons & ~cur) | raw.releasedSince;
    this.latchedPressed |= pressedEdge;
    this.latchedReleased |= releasedEdge;
    // Mouse kökenli saldırı basışı: olay kenarı mouse'tan geldiyse. Yalnız bu tick'te gerçekten "basıldı" sayılan bitler.
    this.latchedMousePressed |= raw.mousePressedSince & pressedEdge & ATTACK_BITS;
    this.prevButtons = cur;
    this.buttons = cur;

    // Hareket: klavye + pad toplamı → ölü bölge/smoothstep → kamera-relative → dünya XZ (|v| ≤ 1).
    applyDeadzone(scratchMove, raw.keyMoveX + raw.padAxisX, raw.keyMoveZ + raw.padAxisY, this.deadzone);
    cameraToWorld(scratchMove, scratchMove.x, scratchMove.z, this.cameraYaw);
    // Klavye kenar freni: pad çubuğu ölü bölge altındaysa hareket tümüyle klavyeden → büyüklüğü tavana kırp (yön korunur).
    if (this.keyEdgeBrake && Math.hypot(raw.padAxisX, raw.padAxisY) < this.deadzone) {
      const m = Math.hypot(scratchMove.x, scratchMove.z);
      if (m > KEY_EDGE_MOVE_MAX) { const s = KEY_EDGE_MOVE_MAX / m; scratchMove.x *= s; scratchMove.z *= s; }
    }
    this.moveX = scratchMove.x; this.moveZ = scratchMove.z;

    // Mouse hareketi: seq değiştiyse "şu anki tick"te hareket etti. (İlk poll'da seq 0 → hareket sayılmaz.)
    if (raw.mouseMoveSeq !== this.lastMouseSeq) {
      this.lastMouseSeq = raw.mouseMoveSeq;
      this.lastMouseMoveTick = this.tick;
    }
    this.mouseNdcX = raw.mouseNdcX; this.mouseNdcY = raw.mouseNdcY;
  }

  /**
   * Bir sim tick'inin girdisi: biriktirilen kenarlar out'a yazılır ve sıfırlanır; buttons/move olduğu gibi kalır
   * (aynı karede 2. tick'te pressed = 0, buttons aynı). Tahsissiz (out parametreli).
   */
  consume(out: InputFrame): void {
    out.buttons = this.buttons;
    out.pressed = this.latchedPressed;
    out.released = this.latchedReleased;
    out.moveX = this.moveX;
    out.moveZ = this.moveZ;
    const fromMouse = this.latchedMousePressed !== 0;
    out.fromMouse = fromMouse;
    // DESIGN §2: aimYaw yalnız (a) mouse son mouseFreshTicks içinde hareket ettiyse ya da (b) saldırı mouse tuşundan geldiyse.
    // (ARCHITECTURE §12 WP6 satırı "30 t" der; DESIGN §2 + AIM.mouseFreshTicks 120 t bağlayıcı — sapma raporlandı.)
    const fresh = this.tick - this.lastMouseMoveTick <= this.mouseFreshTicks;
    out.aimYaw = (fresh || fromMouse) && this.aimResolver !== null ? this.aimResolver(this.mouseNdcX, this.mouseNdcY) : NaN;
    this.latchedPressed = 0;
    this.latchedReleased = 0;
    this.latchedMousePressed = 0;
    this.tick++;
  }

  /** Odak kaybı / duraklatma: tüm basılı bitleri bırakılmış say (kaynaklar da kendi durumunu temizler). */
  reset(): void {
    this.prevButtons = 0; this.buttons = 0;
    this.latchedPressed = 0; this.latchedReleased = 0; this.latchedMousePressed = 0;
    this.moveX = 0; this.moveZ = 0;
  }
}
